import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import type { RolePermissionModel } from '@/adapters/apiv1.g';
import { roleService } from '@/services/role-service';
import type { Page } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import { roleQueryKeys } from './query-keys';

/**
 * List permissions for a specific role.
 * Accepts optional paging args via `opts`.
 */
export function useListRolePermissions(
  tenantId?: UUID,
  roleId?: UUID,
  opts?: {
    limit?: number;
    next?: string;
  }
): UseInfiniteQueryResult<
  InfiniteData<Page<RolePermissionModel>, unknown>,
  Error
> {
  return useInfiniteQuery<
    Page<RolePermissionModel>,
    Error,
    InfiniteData<Page<RolePermissionModel>, unknown>
  >({
    queryKey: roleQueryKeys.permissionList(tenantId, roleId, opts?.limit),
    queryFn: (context: QueryFunctionContext) => {
      if (!tenantId || !roleId) {
        throw new Error('Missing tenant id or role id');
      }
      const pageParam = context.pageParam as string | undefined;
      return roleService.listRolePermissions(tenantId, roleId, {
        limit: opts?.limit,
        next: pageParam,
      });
    },
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
    enabled: !!tenantId && !!roleId,
  });
}
